import React from "react";
import { getInitialsImage } from "src/helpers/helperFunctions";
import PrintStatus from "../GlobalComponents/PrintStatus";
import PatientIcon from "src/assets/images/website-images/patient-icon.png";
import HospitalIcon from "src/assets/images/website-images/hospital-icon.png";
import TimeIcon from "src/assets/images/website-images/time.png";
import DateIcon from "src/assets/images/website-images/date.png";
import GradientBtn from "../WebsiteComponents/GradientBtn";
import { IAppintentType } from "src/types/GlobalInterfaces";
import { Skeleton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { CURRENCY } from "src/lib/constans";

interface IProps {
  doctor: IAppintentType;
  onViewDetails?: () => void;
  type?: "online" | "in-clinic" | "at-home" | "my-doctor" | "prescription";
  skeleton?: boolean;
  id?: string;
  unread?: number;
  isOnline?: boolean;
  timeSplit?: boolean;
}

const LabelandIcon = ({
  label = "",
  icon,
  className,
  size = 3,
  isSplit = true,
}: {
  label?: string | null;
  icon: string;
  className?: string;
  size?: number;
  isSplit?: boolean;
}) => {
  return (
    <div
      className={`flex items-center gap-1 ${
        size === 3 ? "w-[50%] md:w-[30%] min-w-fit" : "w-[50%] md:w-[40%] min-w-fit"
      } ${className}`}
    >
      <img src={icon} alt="" className="w-4 h-4 md:w-[15px] md:h-[15px] lg:w-[1vw] lg:h-[1vw]" />
      <p
        className={`font-size-14px font-Poppins-Regular whitespace-nowrap text-[#3E3E3E] min-w-fit w-[calc(100%-16px)] md:w-[calc(100%-20px)] ${
          isSplit ? "" : "truncate"
        }`}
      >
        {label}
      </p>
    </div>
  );
};

const AppointmentCard: React.FC<IProps> = ({
  doctor,
  onViewDetails,
  type = "online",
  skeleton = false,
  id,
  unread = 0,
  isOnline = false,
  timeSplit = true,
}) => {
  return (
    <div
      className={`shadow-all-light-2 w-full flex flex-col gap-2 2xl:gap-3 p-2 2xl:p-4 rounded-lg ${
        unread > 0 ? "unread-highlight" : "bg-white"
      }`}
    >
      {skeleton ? (
        <>
          <div className="w-full flex justify-between gap-3">
            <div className="flex gap-1 items-center">
              <Skeleton variant="circular" width={60} height={60} />
              <div className="flex flex-col">
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="50%" height={20} />
              </div>
            </div>
            <Skeleton
              variant="rectangular"
              width={80}
              height={24}
              sx={{ borderRadius: 12 }}
            />
          </div>
          <div className="w-full flex flex-wrap gap-2 justify-between">
            <Skeleton variant="rectangular" width="30%" height={24} />
            {(type === "in-clinic" || type === "at-home") && (
              <Skeleton variant="rectangular" width="40%" height={24} />
            )}
            <Skeleton variant="rectangular" width="30%" height={24} />
            <Skeleton variant="rectangular" width="30%" height={24} />
          </div>
          <div className="w-full flex items-center gap-2">
            <Skeleton variant="text" width="10%" height={20} />
            <Skeleton variant="text" width="20%" height={24} />
          </div>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={36}
            sx={{ borderRadius: 8 }}
          />
        </>
      ) : (
        <>
          <div className="w-full flex justify-between gap-3">
            <div className="flex gap-2 items-center">
              <div className="w-[60px] h-[60px] 2xl:w-[70px] 2xl:h-[70px] min-w-[60px] min-h-[60px] 2xl:min-w-[70px] 2xl:min-h-[70px] rounded-full relative">
                <img
                  src={doctor?.image ? doctor?.image : getInitialsImage(doctor?.name, 60, 35, "#00443f")}
                  alt="doctor"
                  onError={(e) => {
                    e.currentTarget.src = getInitialsImage(
                      doctor.name,
                      60,
                      35,
                      "#00443f"
                    );
                  }}
                  className="w-full h-full object-cover rounded-full"
                />
                {isOnline && (
                  <div className="absolute bottom-1 right-2 w-3 h-3 border border-[white] bg-green-500 rounded-full"></div>
                )}
              </div>
              <div className="flex flex-col">
                {type === "at-home" ? (
                  <>
                    <p className="font-size-14px font-Poppins-Regular text-[#00443F] line-clamp-1">
                      {doctor?.serviceCategory}
                    </p>
                    <p className="font-size-16px font-Poppins-Medium text-[#2E2E2E] line-clamp-1">
                      {doctor?.serviceName}
                    </p>
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((v) => (
                        <FontAwesomeIcon
                          key={v}
                          className={`font-size-14px ${
                            doctor.rating && doctor?.rating >= v
                              ? "text-[#FF9B29]"
                              : "text-[#707070]"
                          }`}
                          icon={faStar}
                        />
                      ))}
                      <span className="font-Poppins-Regular ml-2 mb-[-2px] font-size-12px text-[#8D8D8D]">
                        {doctor.rating?.toFixed(1)}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="font-size-18px font-Poppins-Medium text-[#004540]">
                      {doctor?.name}
                    </p>
                    <p className="font-size-14px font-Poppins-Medium text-[#202020] line-clamp-1">
                      {typeof doctor?.specialty !== "string" &&
                        doctor?.specialty?.join(" | ")}
                    </p>
                    <p className="font-size-14px font-Poppins-Regular line-clamp-1 text-[#878787]">
                      {doctor?.qualification || ""}
                    </p>
                  </>
                )}
              </div>
            </div>
            {type !== "my-doctor" && (
              <PrintStatus status={doctor?.status || ""} />
            )}
          </div>
          <div className={`w-full flex flex-wrap gap-2 ${type === "prescription" ? "justify-between" : ""}`}>
            {type !== "at-home" && type !== "my-doctor" && (
              <LabelandIcon
                label={doctor?.patientName}
                icon={PatientIcon}
                size={type === "online" || type === "prescription" ? 3 : 2}
                className={`md:w-[20%] !font-size-2px ${type === "prescription" ? "!w-fit" : ""}`}
              />
            )}
            {type === "in-clinic" && (
              <LabelandIcon
                label={doctor?.hospitalName}
                icon={HospitalIcon}
                size={2}
              />
            )}
            {type !== "my-doctor" && (
              <LabelandIcon
                label={doctor?.time}
                isSplit={!timeSplit}
                icon={TimeIcon}
                size={type === "online" || type === "prescription" ? 3 : 2}
              />
            )}
            <LabelandIcon
              label={doctor?.date}
              icon={DateIcon}
              size={type === "online" || type === "prescription" ? 3 : 2}
            />
          </div>
          {doctor?.fee ? <div className="w-full flex items-center gap-2">
            <p className="font-size-14px font-Poppins-Regular text-[#3E3E3E]">
              Fee:
            </p>
            <p className="font-size-16px font-Poppins-Medium text-[#202020]">
              {CURRENCY} {doctor?.fee ? doctor?.fee?.toFixed(2) : "N/A"}
            </p>
          </div> : null}
          <GradientBtn
            ButtonText={
              type === "my-doctor"
                ? "Manage"
                : type === "prescription"
                ? "View Prescription"
                : "View Details"
            }
            onPress={onViewDetails}
            id={id}
            fontsizeBtn="font-size-18px" // override font size
          />
        </>
      )}
    </div>
  );
};

export default AppointmentCard;
